const keys = {
  authToken: ['merchant_account_token'],
  merchantAccountV2: token => ['merchant_account', 'v2', token],
  merchantV2: (merchantId, token) => ['merchant', 'v2', merchantId, token],
  merchantsSearch: token => ['merchantSearch', token],
  contractV2: {
    findAll: (organizationId, token) => [
      'contractV2',
      'findAll',
      organizationId,
      token,
    ],
  },
};

function injectErrorMetadata(metadata) {
  return error => {
    // eslint-disable-next-line no-param-reassign
    error.metadata = metadata;

    return Promise.reject(error);
  };
}

function authenticatedQuery(queryClient) {
  return configSetupFn => (...args) => {
    const token = queryClient.getQueryData(keys.authToken);

    if (!token) {
      throw new Error('Missing auth token!');
    }

    return configSetupFn(token, ...args);
  };
}

export default function createQueryConfig(queryClient, apiClient) {
  return {
    authToken: () => {
      const queryKey = keys.authToken;

      function fetchToken() {
        return apiClient.authV2
          .getOrCreateAccessToken()
          .then(String)
          .catch(injectErrorMetadata({ queryKey }));
      }

      return {
        queryKey,
        queryFn: fetchToken,
        cacheTime: Infinity,
      };
    },

    merchantAccountV2: authenticatedQuery(queryClient)(token => {
      const queryKey = keys.merchantAccountV2(String(token));

      function fetchMerchantAccount() {
        return apiClient.merchantAccountV2
          .me({
            fields: {
              merchant_account: ['employee'],
              organizations: ['key_account_contract', 'test', 'name'],
            },
            include: ['employee', 'merchants', 'organization'],
          })
          .catch(injectErrorMetadata({ queryKey, token }));
      }

      return {
        queryKey,
        queryFn: fetchMerchantAccount,
      };
    }),

    merchantV2: authenticatedQuery(queryClient)((token, merchantId) => {
      const queryKey = keys.merchantV2(merchantId, String(token));

      function fetchMerchant() {
        const merchants = [
          'feature_toggles',
          'name',
          'organization',
          'created_at',
          'source',
          'ui_visibility',
          'category',
          'pixel_id',
          'slug',
        ];

        const organizations = [
          'id',
          'trial_ends_on',
          'trial_days_left',
          'self_sign_up',
          'test',
          'package_code',
        ];

        return apiClient.merchantV2
          .find(merchantId, {
            include: ['organization'],
            fields: { organizations, merchants },
          })
          .catch(injectErrorMetadata({ merchantId, queryKey, token }));
      }

      return {
        queryKey,
        queryFn: fetchMerchant,
      };
    }),

    merchantsSearch: authenticatedQuery(queryClient)(token => {
      const queryKey = keys.merchantsSearch(String(token));

      function searchMerchant() {
        return apiClient.algolia
          .search({
            keyFilter: [apiClient.algolia.MERCHANTS],
            options: {
              attributesToRetrieve: ['name', 'objectID'],
            },
          })
          .catch(injectErrorMetadata({ queryKey, token }));
      }

      return {
        queryKey,
        queryFn: searchMerchant,
      };
    }),

    contractV2: {
      findAll: authenticatedQuery(queryClient)((token, organizationId) => {
        const queryKey = keys.contractV2.findAll(organizationId, String(token));

        function fetchContract() {
          return apiClient.contractV2
            .findAll({
              filter: { organization: organizationId },
              include: ['packages', 'fees'],
            })
            .catch(injectErrorMetadata({ queryKey, token, organizationId }));
        }

        return {
          queryKey,
          queryFn: fetchContract,
        };
      }),
    },
  };
}
